import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, ArrowLeft, BookOpen, CheckCircle2, Download, FileDown, FileText, Save } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { ouvrirPdf } from '../components/reglement-interieur/SignerReglementDialog';
import { telecharger } from '../components/guide-bc/constantes';
import { ChampsLivret } from '../components/livret-accueil/ChampsLivret';
import { EnvoiLivretCandidats } from '../components/livret-accueil/EnvoiLivretCandidats';

const Bouton = ({ onClick, Icone, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white/10"><Icone className="h-4 w-4" /> {children}</button>
);

export default function LivretAccueil({ user }) {
  const [data, setData] = useState(null);
  const [champs, setChamps] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const charger = useCallback(() => {
    axios.get('/livret-accueil/config').then(({ data: d }) => { setData(d); setChamps(d.champs); }).catch(() => setData({ erreur: true }));
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (!data) return <div className="p-8 text-slate-500">Chargement…</div>;
  if (data.erreur) return <div className="p-8 text-red-600" data-testid="livret-erreur">Accès non autorisé ou erreur de chargement.</div>;
  const edition = ['centre', 'direction'].includes(user?.role);
  const manquants = data.definitions.filter((d) => !(champs[d.cle] || '').trim()).length;

  const enregistrer = async (publie) => {
    setEnvoi(true);
    try {
      const { data: d } = await axios.put('/livret-accueil/config', { champs, publie });
      setData(d);
      setChamps(d.champs);
      if (data.publie && !publie) toast.warning('Livret enregistré mais retiré des candidats : des informations sont à compléter');
      else toast.success(publie ? 'Livret enregistré et publié aux candidats' : 'Livret enregistré');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="livret-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 transition-colors hover:text-[#0F172A]"><ArrowLeft className="h-4 w-4" /> Tableau de bord</Link>
      <header className="rounded-2xl bg-[#334155] p-6 text-white shadow-sm sm:p-8">
        <div className="flex flex-wrap items-center gap-4">
          <div className="rounded-xl bg-white/15 p-3"><BookOpen className="h-7 w-7" /></div>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Administratif CFA</p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Livret d'accueil</h1>
          </div>
          <Bouton onClick={() => ouvrirPdf('/livret-accueil/pdf', 'Livret_accueil_FISL.pdf')} Icone={FileText} testId="livret-voir-pdf">Voir le PDF</Bouton>
          <Bouton onClick={() => ouvrirPdf('/livret-accueil/pdf', 'Livret_accueil_FISL.pdf', 'download')} Icone={Download} testId="livret-telecharger-pdf">Télécharger le PDF</Bouton>
          <Bouton onClick={() => telecharger('/livret-accueil/word', 'Livret_accueil_FISL.docx')} Icone={FileDown} testId="livret-word">Word modifiable</Bouton>
        </div>
        <p className="mt-3 text-sm text-white/70">Livret illustré BPJEPS motonautisme (photos FISL) : présentation, équipe, formation, épreuves expliquées, infos pratiques et horaires, consignes,
          laïcité et Signal-Sports, réclamations et mentions légales. Les informations ci-dessous complètent automatiquement le livret (Word et PDF). Une fois publié, chaque candidat le consulte dès son inscription depuis son espace, vous pouvez le lui envoyer par e-mail (PDF joint) et il rejoint le dossier Qualiopi (indicateurs 9 et 15).</p>
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-semibold ${data.publie ? 'bg-emerald-500/90' : 'bg-white/15'}`} data-testid="livret-statut">
            {data.publie ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {data.publie ? 'Publié aux candidats' : 'Non publié'}
          </span>
          <span className={`text-sm ${manquants ? 'text-amber-300' : 'text-emerald-300'}`} data-testid="livret-manquants">
            {manquants ? `${manquants} information${manquants > 1 ? 's' : ''} à compléter (surlignées en jaune dans le livret)` : 'Toutes les informations sont complétées'}
          </span>
        </div>
      </header>
      {edition && <EnvoiLivretCandidats publie={data.publie} />}
      <ChampsLivret definitions={data.definitions} champs={champs} setChamps={setChamps} edition={edition} />
      {edition && (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-white/95 p-4 shadow-lg backdrop-blur">
          <label className="flex flex-1 items-center gap-3 text-sm font-medium text-slate-700">
            <Switch checked={data.publie} disabled={envoi || (!data.publie && manquants > 0)} onCheckedChange={(v) => enregistrer(v)} data-testid="livret-publier" />
            {manquants && !data.publie ? 'Publication possible quand toutes les informations sont complétées' : 'Publié aux candidats (consultable depuis leur espace)'}
          </label>
          <button type="button" onClick={() => enregistrer(data.publie && !manquants)} disabled={envoi} data-testid="livret-enregistrer"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] px-5 py-2 text-sm font-bold text-white transition-colors hover:bg-[#1E293B] disabled:opacity-50">
            <Save className="h-4 w-4" /> Enregistrer
          </button>
        </div>
      )}
    </div>
  );
}
